// src/pages/QrScanPage.jsx
// الرابط: https://khidma4u.netlify.app/qr?book=SERVICE_ID

import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
// ✅ نستورد supabase من ملفك الموجود مباشرة
import { supabase } from '../services/supabaseService';

function genId() {
  const b = new Uint8Array(20);
  crypto.getRandomValues(b);
  return Array.from(b, x => x.toString(16).padStart(2, '0')).join('');
}

function qrImg(text) {
  return `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(text)}&format=png&ecc=M&margin=10`;
}

const EXPIRE = 300;

export default function QrScanPage() {
  const [params]  = useSearchParams();
  const serviceId = params.get('book');

  const [qr,      setQr]      = useState('');
  const [status,  setStatus]  = useState('loading');
  const [seconds, setSeconds] = useState(EXPIRE);

  const channel = useRef(null);
  const timer   = useRef(null);

  const start = useCallback(async () => {
    if (!serviceId) return;

    // تنظيف القديم
    if (channel.current) { supabase.removeChannel(channel.current); channel.current = null; }
    clearInterval(timer.current);
    setStatus('loading');

    const sid = genId();
    const exp = new Date(Date.now() + EXPIRE * 1000).toISOString();

    const { error } = await supabase
      .from('qr_sessions')
      .insert({ session_id: sid, service_id: serviceId, expires_at: exp });

    if (error) {
      console.error('Supabase insert error:', error);
      setStatus('expired');
      return;
    }

    // رابط QR — هذا ما يمسحه التطبيق
    setQr(qrImg(`https://khidma4u.netlify.app/scan?book=${serviceId}&session=${sid}`));
    setSeconds(EXPIRE);
    setStatus('ready');

    // عداد تنازلي
    timer.current = setInterval(() => {
      setSeconds(s => {
        if (s <= 1) {
          clearInterval(timer.current);
          setStatus('expired');
          if (channel.current) { supabase.removeChannel(channel.current); channel.current = null; }
          return 0;
        }
        return s - 1;
      });
    }, 1000);

    // Realtime — انتظر التطبيق يمسح ويتحقق
    const ch = supabase
      .channel(`qr-${sid}`)
      .on('postgres_changes', {
        event: 'UPDATE', schema: 'public',
        table: 'qr_sessions', filter: `session_id=eq.${sid}`,
      // ✅ بعد — أضف async
}, async ({ new: row }) => {
  if (row.verified) {
    clearInterval(timer.current);
    setStatus('scanned');
    supabase.removeChannel(ch);

    setTimeout(async () => {
      setStatus('done');
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        window.location.href = `/read?book=${serviceId}`;
      } else {
        localStorage.setItem('redirect_after_login', `/read?book=${serviceId}`);
        window.location.href = '/auth';
      }
    }, 1000);
  }
})
      .subscribe();

    channel.current = ch;
  }, [serviceId]);

  useEffect(() => {
    start();
    return () => {
      if (channel.current) supabase.removeChannel(channel.current);
      clearInterval(timer.current);
    };
  }, [start]);

  const fmt = s => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const pct  = Math.round((seconds / EXPIRE) * 100);

  if (!serviceId) return (
    <div style={S.page}>
      <div style={S.errBox}>
        <h2 style={S.errTitle}>⛔ رابط غير صحيح</h2>
        <p style={S.errMsg}>أضف <code>?book=SERVICE_ID</code> للرابط</p>
      </div>
    </div>
  );

  const badge = {
    loading: { color: 'rgba(124,111,255,0.8)', dot: '#7C6FFF', pulse: true,  text: 'جاري التجهيز...' },
    ready:   { color: 'rgba(255,255,255,0.5)',  dot: '#7C6FFF', pulse: true,  text: 'في انتظار المسح من التطبيق' },
    scanned: { color: 'rgba(251,191,36,0.9)',   dot: '#FBBF24', pulse: true,  text: 'تم المسح! جاري فتح الكتاب...' },
    done:    { color: '#10B981',                dot: '#10B981', pulse: false, text: 'تم فتح الكتاب ✓' },
    expired: { color: '#EF4444',                dot: '#EF4444', pulse: false, text: 'انتهت الصلاحية — جدّد الرمز' },
  }[status] ?? { color: '#fff', dot: '#fff', pulse: false, text: '' };

  const qrGlow = (status === 'scanned' || status === 'done')
    ? '0 0 70px rgba(16,185,129,0.55)'
    : '0 0 55px rgba(124,111,255,0.4)';

  return (
    <div style={S.page}>
      {/* خلفية */}
      <div style={S.bgGlow} />

      {/* Header */}
      <div style={S.header}>
        <div style={{ fontSize: 38, marginBottom: 10 }}>📚</div>
        <h1 style={S.h1}>افتح الكتاب على حاسوبك</h1>
        <p style={S.sub}>امسح الرمز من تطبيق خدمة على هاتفك</p>
      </div>

      {/* QR */}
      <div style={{ ...S.qrBox, boxShadow: qrGlow }}>
        {!qr || status === 'loading'
          ? <div style={S.loader}><div style={S.spinner} /></div>
          : <img src={qr} width={260} height={260} alt="QR"
              style={{ display: 'block', borderRadius: 10 }} />
        }
        {(status === 'scanned' || status === 'done') && (
          <div style={S.successLayer}>
            <div style={{ fontSize: 54 }}>✅</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>
              جاري فتح الكتاب...
            </div>
          </div>
        )}
      </div>

      {/* Badge */}
      <div style={{ ...S.badge, color: badge.color, borderColor: badge.color + '44' }}>
        <span style={{
          width: 7, height: 7, borderRadius: '50%',
          background: badge.dot, flexShrink: 0, display: 'inline-block',
          animation: badge.pulse ? 'pdot 1.2s infinite' : 'none',
        }} />
        {badge.text}
      </div>

      {/* شريط الوقت + تجديد */}
      {(status === 'ready' || status === 'expired') && (
        <div style={S.timerWrap}>
          <div style={S.barBg}>
            <div style={{ ...S.barFill, width: `${pct}%` }} />
          </div>
          <span style={S.timerTxt}>
            {status === 'expired' ? 'منتهي الصلاحية' : `ينتهي خلال ${fmt(seconds)}`}
          </span>
          <button style={S.btn} onClick={start}>🔄 تجديد الرمز</button>
        </div>
      )}

      {/* خطوات */}
      {(status === 'ready' || status === 'loading') && (
        <div style={S.steps}>
          {[
            ['١', 'افتح تطبيق خدمة على هاتفك'],
            ['٢', 'اضغط زر مسح QR'],
            ['٣', 'صوّر هذا الرمز — سيُفتح الكتاب هنا تلقائياً'],
          ].map(([n, t]) => (
            <div key={n} style={S.step}>
              <div style={S.stepNum}>{n}</div>
              <p style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', lineHeight: 1.55, margin: 0 }}>{t}</p>
            </div>
          ))}
        </div>
      )}

      {/* CSS animations */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Tajawal:wght@400;700;800;900&display=swap');
        @keyframes pdot { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:.4;transform:scale(0.6);} }
        @keyframes spin  { to{transform:rotate(360deg);} }
      `}</style>
    </div>
  );
}

const S = {
  page: {
    minHeight: '100vh', display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center',
    padding: '40px 24px', gap: 28,
    background: '#080A0E', color: '#fff',
    fontFamily: "'Tajawal', sans-serif", direction: 'rtl',
    position: 'relative',
  },
  bgGlow: {
    position: 'fixed', inset: 0, pointerEvents: 'none',
    background: `
      radial-gradient(ellipse 600px 400px at 20% 30%, rgba(124,111,255,0.08) 0%, transparent 70%),
      radial-gradient(ellipse 400px 600px at 80% 70%, rgba(16,185,129,0.06) 0%, transparent 70%)
    `,
  },
  header: { textAlign: 'center', position: 'relative' },
  h1:     { fontSize: 22, fontWeight: 900, letterSpacing: '-.4px', margin: 0 },
  sub:    { fontSize: 13, color: 'rgba(255,255,255,0.35)', marginTop: 6 },
  qrBox: {
    background: '#fff', borderRadius: 24, padding: 20,
    position: 'relative', transition: 'box-shadow 0.5s',
  },
  successLayer: {
    position: 'absolute', inset: 0,
    background: 'rgba(16,185,129,0.93)', borderRadius: 24,
    display: 'flex', flexDirection: 'column',
    alignItems: 'center', justifyContent: 'center', gap: 10,
  },
  badge: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 20px', borderRadius: 40,
    background: 'rgba(255,255,255,0.04)',
    border: '1px solid',
    fontSize: 13, fontWeight: 700,
    position: 'relative',
  },
  timerWrap: { display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 },
  barBg:     { width: 200, height: 3, background: 'rgba(255,255,255,0.06)', borderRadius: 3, overflow: 'hidden' },
  barFill:   { height: '100%', borderRadius: 3, background: 'linear-gradient(90deg,#7C6FFF,#10B981)', transition: 'width 1s linear' },
  timerTxt:  { fontSize: 11, color: 'rgba(255,255,255,0.2)' },
  btn: {
    marginTop: 4, padding: '7px 18px', borderRadius: 20,
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    color: 'rgba(255,255,255,0.5)', fontSize: 11,
    fontFamily: "'Tajawal', sans-serif", fontWeight: 700, cursor: 'pointer',
  },
  loader:  { width: 300, height: 300, background: 'rgba(255,255,255,0.03)', borderRadius: 24, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  spinner: { width: 36, height: 36, border: '3px solid rgba(255,255,255,0.07)', borderTopColor: '#7C6FFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  steps:   { display: 'flex', flexDirection: 'column', gap: 10, width: '100%', maxWidth: 310, position: 'relative' },
  step:    { display: 'flex', alignItems: 'center', gap: 12, padding: '11px 16px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 14 },
  stepNum: { width: 26, height: 26, borderRadius: 8, flexShrink: 0, background: 'rgba(124,111,255,0.15)', border: '1px solid rgba(124,111,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12, fontWeight: 800, color: '#7C6FFF' },
  errBox:  { background: 'rgba(239,68,68,0.07)', border: '1px solid rgba(239,68,68,0.2)', borderRadius: 20, padding: '28px 32px', textAlign: 'center', maxWidth: 340 },
  errTitle:{ fontSize: 17, fontWeight: 800, color: '#EF4444', marginBottom: 8 },
  errMsg:  { fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.7 },
};